import { Link } from "react-router";
import { useTranslation } from "react-i18next";

import type { HomeAlert as Alert } from "@/entities/home";
import { ROUTES } from "@/shared/config";
import { Icon, type IconName } from "@/shared/ui";

/**
 * Figma: компонент `Banner` (15:60). Документация компонента дословно:
 * «Triggered, contextual messages. Never imply guaranteed returns.»
 *
 * `title` и `body` приходят с бэкенда готовыми по-русски — рендерим как есть
 * и в `ru.json` не заводим.
 *
 * `alert.robot_id` — это id КУПЛЕННОГО робота (`user_robot`), а не робота из
 * каталога, поэтому ссылка ведёт в «мои роботы». Поле опционально: без него
 * баннер остаётся некликабельным, а не ломается.
 */
type Tone = "loss" | "profit";

const TONES: Record<Tone, { border: string; badge: string; link: string; icon: IconName }> = {
  loss: {
    border: "border-status-loss",
    badge: "bg-status-loss",
    link: "text-status-loss",
    icon: "warningFill",
  },
  profit: {
    border: "border-status-profit",
    badge: "bg-status-profit",
    link: "text-status-profit",
    // Пересечённая отметка прибыли — это про рост, а не про «звёздочку».
    icon: "trendUpFill",
  },
};

export function HomeAlert({ alert }: { alert: Alert }) {
  const { t } = useTranslation();
  const tone = TONES[alert.state === "loss_alert" ? "loss" : "profit"];

  const body = (
    <>
      <span className={`flex size-9 shrink-0 items-center justify-center rounded-xl ${tone.badge}`}>
        {/* Дефект макета: глиф покрашен в цвет подложки и невидим (DESIGN.md §9). */}
        <Icon name={tone.icon} size={20} className="text-text-inverse" />
      </span>
      <div className="flex flex-col gap-[5px]">
        <span className="text-body font-semibold text-text-primary">{alert.title}</span>
        <span className="text-label font-normal text-text-secondary">{alert.body}</span>
        {alert.robot_id == null ? null : (
          <span className={`text-caption font-semibold ${tone.link}`}>{t("home.alertLink")} →</span>
        )}
      </div>
    </>
  );

  // Предупреждение приходит вместе с данными главной и не должно возникать
  // рывком посреди уже собранного экрана (FE-13).
  const className = `animate-rise-in press-row flex w-full items-start gap-3 rounded-[20px] border bg-background-elevated p-4 ${tone.border}`;

  return alert.robot_id == null ? (
    <section className={className}>{body}</section>
  ) : (
    <Link to={ROUTES.myRobot(alert.robot_id)} className={className}>
      {body}
    </Link>
  );
}
